import { huge, type HugeNumber } from "@/shared/numbers";
import {
  normalizeNHovGrowthExponentBonus,
  resolveNHovBranchEffectiveLevel,
} from "./nHovBranchFormulaMath";
import type { NHovHngScaleBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export const HOV_CELL_RANK_SCALE_BASE = 10;

export const NHOV_HNG_SCALE_BASE_MULTIPLIER_PER_LEVEL = 1.11;
export const NHOV_HNG_SCALE_EFFECTIVE_LEVEL_EXPONENT = 0.8;

export interface ResolveHovCellRankScaleBaseOptions {
  readonly hngScaleLevel: number;
  // Additive bonus applied to HOV_CELL_RANK_SCALE_BASE before the NHov
  // hngScale branch multiplier. Resolved from acquired Hov skills upstream.
  readonly hngRankScaleBaseBonus?: number;
  // Additive bonus applied to the hngScale branch growth exponent. Resolved
  // from acquired Hov skills upstream.
  readonly exponentBonus?: number;
  readonly balance?: NHovHngScaleBalance;
}

export interface CalculateHovCellRankMultiplierOptions {
  readonly cellIndex: number;
  readonly hngScaleLevel: number;
  readonly hngRankScaleBaseBonus?: number;
  readonly exponentBonus?: number;
  readonly balance?: NHovHngScaleBalance;
}

function isFiniteNonNegative(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

function normalizeRankScaleBaseBonus(value: number | undefined): number {
  if (value === undefined) return 0;
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value;
}

// HugeNumber-preserving core so the rank scale base grows past the JS number
// limit once the `outside-nhov` cap is lifted.
export function resolveHovCellRankScaleBaseHuge(
  options: ResolveHovCellRankScaleBaseOptions,
): HugeNumber {
  const bonus = normalizeRankScaleBaseBonus(options.hngRankScaleBaseBonus);
  const baseWithBonus =
    (options.balance?.rankScaleBase ?? HOV_CELL_RANK_SCALE_BASE) + bonus;

  if (
    !isFiniteNonNegative(options.hngScaleLevel) ||
    options.hngScaleLevel <= 0
  ) {
    return huge(baseWithBonus);
  }

  const effectiveLevel = resolveNHovBranchEffectiveLevel({
    level: options.hngScaleLevel,
    exponent:
      (options.balance?.effectiveLevelExponent ??
        NHOV_HNG_SCALE_EFFECTIVE_LEVEL_EXPONENT) +
      normalizeNHovGrowthExponentBonus(options.exponentBonus),
  });
  return huge(
    options.balance?.multiplierPerLevel ??
      NHOV_HNG_SCALE_BASE_MULTIPLIER_PER_LEVEL,
  )
    .pow(effectiveLevel)
    .mul(baseWithBonus);
}

// HugeNumber-preserving core so per-cell rank multipliers grow past the JS
// number limit once the `outside-nhov` cap is lifted.
export function calculateHovCellRankMultiplierHuge(
  options: CalculateHovCellRankMultiplierOptions,
): HugeNumber {
  if (!Number.isFinite(options.cellIndex) || options.cellIndex <= 0) {
    return huge(1);
  }

  const cellRank = Math.floor(options.cellIndex) + 1;
  const rankScaleBase = resolveHovCellRankScaleBaseHuge({
    hngScaleLevel: options.hngScaleLevel,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    exponentBonus: options.exponentBonus,
    balance: options.balance,
  });
  return rankScaleBase.pow(cellRank - 1);
}
